import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Tag,
  Space,
  message,
  Input,
  Select,
  Tooltip,
  Popconfirm,
  Typography,
  Card,
  Statistic,
  Row,
  Col,
} from "antd";
import request from "../../../utils/request";
import { useGlobal } from "../../../contexts/GlobalContext";
import {
  AdminPodcastInfo,
  PodcastStatus,
  SPEAKER_TYPES,
} from "../../../types/podcast";

const { Search } = Input;
const { Text } = Typography;

const PodcastTab: React.FC = () => {
  const { API_BASE_URL, token } = useGlobal();
  const [loading, setLoading] = useState(false);
  const [podcasts, setPodcasts] = useState<AdminPodcastInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<number>(-1);
  const [openIdFilter, setOpenIdFilter] = useState<string>("");
  const [stats, setStats] = useState<any>({});
  const [cleanLoading, setCleanLoading] = useState(false);

  // 获取播客统计
  const fetchStats = useCallback(async () => {
    try {
      const response = await request.get(
        `${API_BASE_URL}/api/v3/admin/podcast/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        setStats(response.data.data);
      }
    } catch (error: any) {
      console.error("获取统计数据失败:", error);
    }
  }, [API_BASE_URL, token]);

  // 获取播客列表
  const fetchPodcasts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: pageSize,
        status: statusFilter,
      };

      if (openIdFilter) {
        params.open_id = parseInt(openIdFilter);
      }

      const response = await request.get(
        `${API_BASE_URL}/api/v3/admin/podcast/list`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        setPodcasts(response.data.data.list || []);
        setTotal(response.data.data.total || 0);
      } else {
        message.error(response.data.msg || "获取播客列表失败");
      }
    } catch (error: any) {
      console.error("获取播客列表失败:", error);
      message.error(error.response?.data?.msg || "获取播客列表失败");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, currentPage, pageSize, statusFilter, openIdFilter]);

  useEffect(() => {
    fetchStats();
    fetchPodcasts();
  }, [fetchStats, fetchPodcasts]);

  // 清理过期播客
  const handleCleanExpired = async () => {
    setCleanLoading(true);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/podcast/clean`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success(
          `成功清理 ${response.data.data.cleaned_count || 0} 条过期播客`
        );
        fetchStats();
        fetchPodcasts();
      } else {
        message.error(response.data.msg || "清理失败");
      }
    } catch (error: any) {
      console.error("清理失败:", error);
      message.error(error.response?.data?.msg || "清理失败");
    } finally {
      setCleanLoading(false);
    }
  };

  // 下载播客
  const handleDownload = async (podcastId: number, title: string) => {
    try {
      message.loading({ content: "正在准备下载...", key: "download" });
      const response = await request.get(
        `${API_BASE_URL}/api/v3/admin/podcast/download`,
        {
          params: { podcast_id: podcastId },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        const data = response.data.data;
        // 将Base64转换为Blob
        const byteCharacters = atob(data.audio_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: `audio/${data.audio_format}`,
        });

        // 创建下载链接
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${title}.${data.audio_format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success({ content: "下载成功", key: "download" });
      } else {
        message.error({
          content: response.data.msg || "下载失败",
          key: "download",
        });
      }
    } catch (error: any) {
      console.error("下载失败:", error);
      message.error({
        content: error.response?.data?.msg || "下载失败",
        key: "download",
      });
    }
  };

  // 获取状态标签
  const getStatusTag = (status: number, errorMsg?: string) => {
    switch (status) {
      case PodcastStatus.Pending:
        return <Tag color="default">等待中</Tag>;
      case PodcastStatus.Processing:
        return <Tag color="processing">生成中</Tag>;
      case PodcastStatus.Completed:
        return <Tag color="success">已完成</Tag>;
      case PodcastStatus.Failed:
        return (
          <Tooltip title={errorMsg || "生成失败"}>
            <Tag color="error">失败</Tag>
          </Tooltip>
        );
      default:
        return <Tag>未知</Tag>;
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "用户ID",
      dataIndex: "open_id",
      key: "open_id",
      width: 100,
    },
    {
      title: "标题",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: "摘要ID",
      dataIndex: "summary_id",
      key: "summary_id",
      width: 100,
    },
    {
      title: "发音人",
      dataIndex: "speaker_type",
      key: "speaker_type",
      width: 100,
      render: (type: string) =>
        SPEAKER_TYPES[type as keyof typeof SPEAKER_TYPES] || type,
    },
    {
      title: "时长",
      dataIndex: "duration",
      key: "duration",
      width: 100,
      render: (duration?: number) => {
        if (!duration) return "-";
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
      },
    },
    {
      title: "Token消耗",
      dataIndex: "token_used",
      key: "token_used",
      width: 100,
      render: (token?: number) => token?.toLocaleString() || "-",
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: number, record: AdminPodcastInfo) =>
        getStatusTag(status, record.error_msg),
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: "操作",
      key: "action",
      width: 120,
      fixed: "right" as const,
      render: (_: any, record: AdminPodcastInfo) => (
        <Space size="small">
          {record.status === PodcastStatus.Completed && (
            <Button
              type="link"
              size="small"
              icon={<i className="ri-download-line" />}
              onClick={() => handleDownload(record.id, record.title)}
            >
              下载
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic title="总播客数" value={stats.total || 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="等待中"
              value={stats.pending || 0}
              valueStyle={{ color: "#8c8c8c" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="生成中"
              value={stats.processing || 0}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已完成"
              value={stats.completed || 0}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            <Search
              placeholder="输入用户ID搜索"
              allowClear
              style={{ width: 200 }}
              value={openIdFilter}
              onChange={(e) => setOpenIdFilter(e.target.value)}
              onSearch={() => {
                setCurrentPage(1);
                fetchPodcasts();
              }}
            />
            <Select
              style={{ width: 120 }}
              placeholder="筛选状态"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
            >
              <Select.Option value={-1}>全部</Select.Option>
              <Select.Option value={PodcastStatus.Pending}>
                等待中
              </Select.Option>
              <Select.Option value={PodcastStatus.Processing}>
                生成中
              </Select.Option>
              <Select.Option value={PodcastStatus.Completed}>
                已完成
              </Select.Option>
              <Select.Option value={PodcastStatus.Failed}>失败</Select.Option>
            </Select>
            <Button
              onClick={fetchPodcasts}
              icon={<i className="ri-refresh-line" />}
            >
              刷新
            </Button>
          </Space>
          <Popconfirm
            title="确定要清理所有过期的播客吗？"
            description="此操作将删除所有过期的播客记录和文件"
            onConfirm={handleCleanExpired}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              loading={cleanLoading}
              icon={<i className="ri-delete-bin-line" />}
            >
              清理过期播客
            </Button>
          </Popconfirm>
        </Space>
      </Card>

      {/* 播客列表 */}
      <Table
        columns={columns}
        dataSource={podcasts}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
        scroll={{ x: 1400 }}
      />
    </div>
  );
};

export default PodcastTab;
